import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Formik, FormikValues } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Wrap } from './styled';
import SignUpForm from './Form';

import SAVE_PROFILE from '@graphql/mutations/saveProfile';
import SIGN_UP from '@graphql/mutations/signUp';
import Success from '@components/Success';
import { addNotificationActionCreator } from '@store/notifications/actions';
import { NotificationType } from '@store/notifications/types';
import { email } from '@utils/validation';
import { MINIMAL_AGE } from '@utils/config';
import { getInvalidCharsMessage } from '@components/Auth/InvalidCharsMessage';
import history from '@utils/history';
import { buildSignUpParameters, getSignUpFormValues } from '@utils/user';
import { buildProfileParameters } from '@utils/profile';
import { LOGIN, PROMOTIONS } from '@utils/paths';
import { isLogin as isLoginSelector } from '@store/auth/selectors';

const SignUp = () => {
  const UNIQUE_USER_ERROR_CODE = 'InvalidUniqueEntityException';
  const { t } = useTranslation();
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signUp] = useMutation(SIGN_UP);
  const [saveProfile] = useMutation(SAVE_PROFILE);
  const dispatch = useDispatch();

  const isLogin = useSelector(isLoginSelector);

  if (isLogin) {
    history.push(PROMOTIONS);
  }

  const validationSchema = Yup.object().shape({
    email: email(true, t('validation.error.invalidEmail')),
    firstName: Yup.string()
      .required(t('validation.error.required'))
      .matches(/^[^0-9]+$/, t('validation.error.numbersNotAllowed')),
    lastName: Yup.string()
      .required(t('validation.error.required'))
      .matches(/^[^0-9]+$/, t('validation.error.numbersNotAllowed')),
    gender: Yup.string().required(t('validation.error.required')),
    birthDate: Yup.string()
      .required(t('validation.error.required'))
      .test('birthDate', t('validation.error.invalidBirthDate'), (value) => {
        return moment().diff(moment(value, 'DD/MM/YYYY'), 'years') >= MINIMAL_AGE;
      }),
    password: Yup.string()
      .required(t('validation.error.required'))
      .test({
        name: 'invalid-chars',
        message: ({ value }) => getInvalidCharsMessage(value) || '',
        test: (value) => {
          if (!value) return true;
          return !getInvalidCharsMessage(value);
        },
      })
      .matches(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[¡!#$%&()*+,\-./:;<=>¿?@[\]\\^_{|}~])[A-Za-z\d¡!#$%&()*+,\-./:;<=>¿?@[\]\\^_{|}~]{10,255}$/u,
        t('forms.confirmPassword.error.regexp')
      ),
    confirmedPassword: Yup.string()
      .required(t('validation.error.required'))
      .oneOf([Yup.ref('password')], t('forms.confirmPassword.error.match')),
    policies: Yup.boolean().oneOf([true], t('validation.error.policies')),
  });

  return (
    <Wrap>
      {isRegistered ? (
        <Success
          onClose={() => {}}
          title={t('pages.successSignUp.caption.title')}
          text={t('pages.successSignUp.texts.success')}
          back={t('pages.successSignUp.texts.back')}
        />
      ) : (
        <Formik
          initialValues={{
            email: '',
            password: '',
            confirmedPassword: '',
            firstName: '',
            lastName: '',
            gender: '',
            birthDate: '',
            policies: false,
            communicationsPolicy: false,
          }}
          validationSchema={validationSchema}
          /* eslint-disable */
          onSubmit={(values: FormikValues) => {
            setIsLoading(true);
            const variablesMutation = buildSignUpParameters(getSignUpFormValues(values));
            signUp({ variables: variablesMutation })
              .then((res) => {
                const { data } = res;
                const { signup } = data;
                const { id } = signup;
                saveProfile({ variables: { userId: id, ...buildProfileParameters(values) } })
                  .then(() => {
                    return setIsRegistered(true);
                  })
                  .catch(() => {
                    setIsLoading(false);
                    dispatch(
                      addNotificationActionCreator({
                        type: NotificationType.Error,
                        title: t('forms.signUp.error.profile'),
                      })
                    );
                  });
              })
              .catch((e) => {
                const { graphQLErrors } = e;
                const firstError = graphQLErrors.pop();
                setIsLoading(false);
                const isUniqueUserError = firstError?.extensions?.code === UNIQUE_USER_ERROR_CODE;
                const errorMsg = isUniqueUserError ? t('forms.signUp.error.unique') : t('forms.signUp.error.network');
                return dispatch(
                  addNotificationActionCreator({
                    type: NotificationType.Error,
                    title: errorMsg,
                  })
                );
              });
          }}
        >
          <SignUpForm handleSignInClick={() => history.push(LOGIN)} isLoading={isLoading} />
        </Formik>
      )}
    </Wrap>
  );
};

export default SignUp;
